/**
 * Plain-language wording for the run page's Quality tab: lenses, dimensions,
 * verdicts, severities and rules, plus the score formatting rules (null → "—",
 * never 0). Kept out of the components so every Quality panel says the same thing.
 */
import type { CheckScore, ScoreSummary, VerdictMovement } from '../types/quality-scores.types';
import { SCORE_LENSES } from '../types/quality-scores.types';

import type { StatusTone } from './run-status';
import { humanizeEnum } from './run-status';

/** Channels the quality scorer has rules for — the picker's fallback list when a 422 hides the real one. */
export const KNOWN_SCORE_PLATFORMS = ['GOOGLE_MERCHANT_CENTER', 'META_COMMERCE', 'AMAZON'] as const;

/** What one business view (lens) means, for a merchandiser. */
export interface LensInfo {
  name: string;
  short: string;
  explanation: string;
}

const LENS_INFO: Record<string, LensInfo> = {
  SEO: {
    name: 'Search & Shopping',
    short: 'SEO',
    explanation: 'How well products rank in Google Shopping and ordinary search results.',
  },
  AEO: {
    name: 'AI answers',
    short: 'AEO',
    explanation: 'How likely a product is to be the direct answer in AI Overviews.',
  },
  GEO: {
    name: 'AI recommendations',
    short: 'GEO',
    explanation: 'How likely generative AI assistants are to quote and recommend a product.',
  },
};

/** Lens code → name + one-line explanation; unknown lenses fall back to their code. */
export function lensInfo(lens: string): LensInfo {
  return LENS_INFO[lens] ?? { name: lens, short: lens, explanation: '' };
}

/** Lens codes in the canonical SEO → AEO → GEO order, then anything new the service adds. */
export function orderLenses(keys: Iterable<string>): string[] {
  const set = new Set(keys);
  const ordered: string[] = SCORE_LENSES.filter((lens) => set.has(lens));
  return [...ordered, ...[...set].filter((key) => !ordered.includes(key))];
}

/** Lenses either phase of the summary was scored on — what this channel's "View as" offers. */
export function summaryLenses(summary: ScoreSummary): string[] {
  return orderLenses([
    ...Object.keys(summary.pre?.lenses ?? {}),
    ...Object.keys(summary.post?.lenses ?? {}),
  ]);
}

/** The Quality tab's "View as" choices: every rule together, or one business view. Mirrored to `?qview=`. */
export const QUALITY_VIEWS = ['overall', ...SCORE_LENSES] as const;
export type QualityView = (typeof QUALITY_VIEWS)[number];

/** A lens's tab label — its plain name ("AI answers"); the short code is never shown. */
export function lensTabLabel(lens: string): string {
  const info = lensInfo(lens);
  return info.name;
}

/** One sentence under the "View as" switch saying what the chosen view scores. */
export function viewDescription(lens: string | null): string {
  if (lens === null) {
    return 'Every rule for this channel, scored together — the overall quality of your content.';
  }
  const info = lensInfo(lens);
  return `${info.explanation || info.name} Only the rules that count for ${info.name} are scored in this view.`;
}

/** Who checks a rule (`scorer`), in words: a fixed rule, a trained model, or an AI reading it like a reviewer. */
export const SCORER_ORDER = ['DETERMINISTIC', 'MODEL', 'LLM'] as const;

/** Scorer code → plain label + what it means; unknown scorers are humanised. */
export function scorerInfo(scorer: string): { label: string; explanation: string } {
  switch (scorer) {
    case 'DETERMINISTIC':
      return {
        label: 'Rules',
        explanation:
          'Checked by a fixed rule (length, spelling, banned words) — same text, same result.',
      };
    case 'MODEL':
      return {
        label: 'AI model',
        explanation:
          'Checked by a trained model that measures things like readability or relevance.',
      };
    case 'LLM':
      return {
        label: 'AI judge',
        explanation: 'Checked by an AI that reads the text the way a reviewer would.',
      };
    default:
      return { label: humanizeEnum(scorer), explanation: '' };
  }
}

/** Scorer codes in canonical order (Rules → AI model → AI judge), then anything new. */
export function orderScorers(keys: Iterable<string>): string[] {
  const set = new Set(keys);
  const ordered: string[] = SCORER_ORDER.filter((s) => set.has(s));
  return [...ordered, ...[...set].filter((key) => !ordered.includes(key))];
}

/** A 0–1 share as a whole percentage ("71%"); "—" when there is nothing to divide. */
export function formatPercent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return `${Math.round(rate * 100).toLocaleString()}%`;
}

const DIMENSION_INFO: Record<string, { name: string; explanation: string }> = {
  COMPLETENESS: {
    name: 'Complete',
    explanation: 'Is the content there, and long enough to say what it needs to?',
  },
  CORRECTNESS: {
    name: 'Accurate',
    explanation: 'Does it mention the right facts — brand, colour, sizes — from your catalog?',
  },
  COMPLIANCE: {
    name: 'Follows channel rules',
    explanation: 'Length limits, no promotional text, no shouting in capitals.',
  },
  SECURITY: {
    name: 'Safe & clean',
    explanation: 'No links, code or contact details that a channel would reject.',
  },
  READABILITY: {
    name: 'Easy to read',
    explanation: 'Plain sentences and varied wording a shopper can skim.',
  },
  RELEVANCE: {
    name: 'On topic',
    explanation: 'Says what the product is early and covers its key attributes.',
  },
};

/** Dimension code → plain name + explanation (humanised fallback). */
export function dimensionInfo(dimension: string): { name: string; explanation: string } {
  return DIMENSION_INFO[dimension] ?? { name: humanizeEnum(dimension), explanation: '' };
}

/** Verdict code → label, tone and what it means. */
export function verdictInfo(verdict: string): {
  label: string;
  tone: StatusTone;
  explanation: string;
} {
  switch (verdict) {
    case 'PASS':
      return {
        label: 'Pass',
        tone: 'success',
        explanation: 'Scores 90 or more with no critical problems — ready to publish.',
      };
    case 'REVIEW':
      return {
        label: 'Needs review',
        tone: 'warning',
        explanation: 'Scores between 70 and 90 — usable, but worth a look.',
      };
    case 'FAIL':
      return {
        label: 'Fail',
        tone: 'error',
        explanation: 'Scores below 70, or has a critical problem such as a missing title.',
      };
    default:
      return {
        label: 'Not scored',
        tone: 'muted',
        explanation: 'This step did not score the product.',
      };
  }
}

/** Severity code → word, tone and the points it costs. */
export function severityInfo(severity: string): {
  label: string;
  tone: StatusTone;
  explanation: string;
} {
  switch (severity) {
    case 'CRITICAL':
      return {
        label: 'Critical',
        tone: 'error',
        explanation: 'Costs 25 points and fails the product on its own.',
      };
    case 'MAJOR':
      return { label: 'Major', tone: 'warning', explanation: 'Costs 10 points.' };
    case 'MINOR':
      return { label: 'Minor', tone: 'muted', explanation: 'Costs 5 points.' };
    default:
      return { label: humanizeEnum(severity), tone: 'muted', explanation: '' };
  }
}

/** Rule kind (the part of a check id after the field) → the problem in words. */
const RULE_PROBLEM: Record<string, string> = {
  present: 'is missing',
  length_min: 'is too short',
  length_max: 'is too long',
  uses_the_space: "doesn't use the space shoppers see",
  no_promotional_text: 'has promotional text ("sale", "free shipping")',
  no_emoji: 'has emoji',
  no_markup: 'has HTML or code in it',
  no_all_caps: 'uses too many capital letters',
  no_repeated_punctuation: 'has repeated punctuation ("!!", "???")',
  no_keyword_stuffing: 'repeats the same words too often',
  has_brand: "doesn't mention the brand",
  brand_up_front: "doesn't put the brand near the start",
  has_color: "doesn't mention the colour",
  has_material: "doesn't mention the material",
  has_volume: "doesn't mention the size or volume",
  no_links: 'has links in it',
  no_contact_details: 'has an email address or phone number',
  no_superlatives: 'makes claims like "best" or "amazing"',
  uk_spelling: 'uses US spelling',
  readability: 'is hard to read',
  covers_catalog_terms: 'leaves out catalog facts (brand, colour, material)',
  answer_first: "doesn't say what the product is in the first sentence",
  states_facts: 'has few concrete facts (sizes, amounts)',
  varied_wording: 'repeats the same wording',
  covers_attributes: 'leaves out key product attributes',
  depth_min: 'is too general — needs a more specific path',
  count: 'has the wrong number of points',
  item_length_max: 'has a point that is too long',
  unique: 'repeats an item',
  // Kinds added when the scorer began checking every channel field (identifiers,
  // attributes, highlights, product detail rows, dimensions, images).
  agrees_with_title: "doesn't agree with the title",
  ai_disclosure: "doesn't say it was written by AI",
  bare_values: 'has "name: value" pairs instead of plain values',
  casing: 'uses the wrong capitals (names in Title Case, values in sentence case)',
  closed_list: "isn't one of the channel's standard values",
  count_target: 'has too few or too many items to show well',
  enum: "isn't a value the channel accepts",
  first_is_measurable: "doesn't start with a measurement",
  first_sentence_short: 'opens with a sentence that is too long',
  fits: 'is too long to fit where it is shown',
  fits_the_card: 'is too long for the product card',
  format: "isn't a measurement with a valid unit",
  grouped: 'has rows outside their section',
  has_gender: "doesn't say who it's for",
  has_model_or_spec: "doesn't mention a model number or headline spec",
  https: "isn't a secure (https) link",
  identified: 'has no barcode (GTIN) or part number (MPN) to identify it',
  in_taxonomy: "isn't a real Google category",
  item_length: 'has an item that is too long',
  item_words: 'has an item with too many words',
  key_spec_early: "doesn't give the key spec near the start",
  lengths: 'has a row that is too long',
  mentions_color: "doesn't mention the colour",
  mentions_material: "doesn't mention the material",
  mirrors_description: "doesn't match the description",
  mirrors_title: "doesn't match the title",
  names_concentration: "doesn't name the concentration (EDP, EDT…)",
  names_gender_early: "doesn't say early on who it's for",
  no_banned: 'has promotional wording',
  no_comparisons: 'compares the product with others',
  no_duplicates: 'repeats a row',
  no_extra_whitespace: 'has extra spaces',
  no_fibre_codes: 'uses fibre codes ("PES") instead of names',
  no_filler_adjectives: 'uses filler words like "stylish" or "perfect"',
  no_invented_numbers: "has numbers that aren't in your feed",
  no_junk: 'has a code, number or placeholder instead of a value',
  no_lily_section: 'has a catch-all section',
  no_medical_claims: 'makes medical or miracle claims',
  no_multi: 'says "multi" or "assorted" instead of naming the colour',
  no_placeholders: 'has placeholder values',
  no_stock_opener: 'opens with a stock line',
  no_vertical_opener: 'opens with a stock line',
  not_for_this_vertical: "is set, but doesn't apply to this kind of product",
  not_google_wording: "copies Google's category wording",
  not_lookalike: 'names a lookalike ("faux", "-look") as the material',
  not_on_formulated: "is set, but doesn't apply to a formulated product",
  not_on_hard_goods: "is set, but doesn't apply to hard goods",
  not_placeholder: 'is a placeholder',
  not_title_echo: 'repeats what the title already says',
  parts: 'lists too many values',
  path_or_id: 'mixes a category id with a path',
  positive: "isn't a positive whole number",
  required_rows: 'is missing rows this kind of product needs',
  rows_max: 'has too many rows',
  rows_min: 'has too few rows',
  same_unit: 'mixes units across the measurements',
  separator: 'doesn\'t separate levels with " > "',
  short: 'has items that are too wordy',
  single: 'has more than one value',
  states_customisation: "doesn't say how it can be printed or branded",
  states_dimensions: "doesn't give the dimensions",
  states_light_spec: "doesn't give the fitting or light output",
  states_metal_or_spec: "doesn't name the metal, stones or a key spec",
  states_specs: "doesn't give a technical spec",
  states_use: "doesn't say what it's for",
  subfields: 'has rows missing a section, name or value',
  units: 'has measurements without a unit',
  use_case_rows: 'has too few, or too vague, use-case rows',
  valid: "isn't a valid barcode",
};

/**
 * Whole sentences for checks whose meaning depends on the field they sit on — cross-field
 * checks hosted on one member of a set, where "<field> <problem>" would mislead.
 */
const RULE_SENTENCE: Record<string, string> = {
  'shipping.all_or_none': 'Shipping size is only partly filled in — give all four or none',
  'shipping.not_copied': "Shipping size copies the product's size — it should be the packed box",
  'product_dimensions.same_unit': 'Product dimensions mix units',
  'is_bundle.agrees_with_detection': "Bundle doesn't match what the product looks like",
  // `count` reads "points" for bullets; these two count images and features.
  'additional_image_link.count': 'More images has too many or too few images',
  'additional_features.count': 'Additional features has too many or too few values',
};

/** Plain names for scored fields whose code doesn't read well humanised ("Gtin", "Mpn"). */
const FIELD_LABEL: Record<string, string> = {
  BULLETS: 'Bullet points',
  STRUCTURED_TITLE: 'Structured title',
  STRUCTURED_DESCRIPTION: 'Structured description',
  PRODUCT_HIGHLIGHT: 'Highlights',
  PRODUCT_DETAIL: 'Product details',
  GOOGLE_PRODUCT_CATEGORY: 'Google product category',
  GTIN: 'Barcode (GTIN)',
  MPN: 'Part number (MPN)',
  IDENTIFIER_EXISTS: 'Has a barcode',
  ITEM_GROUP_ID: 'Variant group',
  COLOR: 'Colour',
  IS_BUNDLE: 'Bundle',
  IMAGE_LINK: 'Main image',
  ADDITIONAL_IMAGE_LINK: 'More images',
};

/** The field a check is on, as a word ("Description", "Barcode (GTIN)"). */
export function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? humanizeEnum(field);
}

/**
 * A rule id (`description.length_min`) as the problem it catches — "Description is
 * too short" — so the Rules list reads as findings, not config keys.
 */
export function ruleLabel(checkId: string, field?: string): string {
  const sentence = RULE_SENTENCE[checkId];
  if (sentence) return sentence;
  const dot = checkId.indexOf('.');
  const prefix = dot >= 0 ? checkId.slice(0, dot) : '';
  const kind = dot >= 0 ? checkId.slice(dot + 1) : checkId;
  const subject = fieldLabel(field && field.length > 0 ? field : prefix.toUpperCase());
  const problem = RULE_PROBLEM[kind];
  if (problem) return `${subject} ${problem}`;
  return `${subject}: ${humanizeEnum(kind).toLowerCase()}`;
}

/** A detected product group as words: No match, General (the shared instructions), else its name. */
export function verticalLabel(vertical: string): string {
  if (!vertical) return 'Not detected';
  if (vertical === 'NO_MATCH') return 'No match';
  if (vertical === 'COMMON') return 'General';
  return humanizeEnum(vertical);
}

/** A 0–100 score for display: one decimal at most, "—" when there is nothing to show (never 0). */
export function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return '—';
  return Number(score.toFixed(1)).toLocaleString();
}

/** A before→after change with its sign ("+30.5", "−4"); "—" when either side is missing. */
export function formatDelta(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return '—';
  const rounded = Number(delta.toFixed(1));
  if (rounded === 0) return '±0';
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toLocaleString()}`;
}

/** A 0–100 score always with one decimal ("87.0", not "87") so columns of scores line up; "—" when missing. */
export function formatScore1(score: number | null | undefined): string {
  if (score === null || score === undefined) return '—';
  return score.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** A signed score change always with one decimal ("+3.0", "−4.5", "±0.0"); "—" when missing. */
export function formatDelta1(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return '—';
  const rounded = Number(delta.toFixed(1));
  if (rounded === 0) return '±0.0';
  return `${rounded > 0 ? '+' : '−'}${formatScore1(Math.abs(rounded))}`;
}

/** The scorer's score gates — the one copy every Quality panel reads: below 70 fails, 70–90 needs review, 90+ passes. */
export const REVIEW_GATE = 70;
export const PASS_GATE = 90;

/**
 * Score zone → tone: 90+ good, 70–90 needs a look, below 70 poor; muted when unknown. Every
 * score mark (number, gauge, bar, bullet, dot) uses this — never the verdict — so an 87.9 reads amber.
 */
export function scoreTone(score: number | null | undefined): StatusTone {
  if (score === null || score === undefined) return 'muted';
  if (score >= PASS_GATE) return 'success';
  if (score >= REVIEW_GATE) return 'warning';
  return 'error';
}

const VERDICT_RANK: Record<string, number> = { FAIL: 0, REVIEW: 1, PASS: 2 };

/**
 * True when a verdict is worse than its score's zone — a Fail at 70+ or a Needs review at 90+ —
 * i.e. a critical problem or a weak field holds the product down whatever its score.
 */
export function isVerdictBlocked(verdict: string, score: number | null | undefined): boolean {
  const band = bandVerdict(score);
  const rank = VERDICT_RANK[verdict];
  if (band === null || rank === undefined) return false;
  return rank < (VERDICT_RANK[band] ?? 0);
}

/** A blocked verdict's pill text: "Fail · blocked" / "Needs review · held". */
export function blockedVerdictLabel(verdict: string): string {
  return verdict === 'FAIL' ? 'Fail · blocked' : `${verdictInfo(verdict).label} · held`;
}

/**
 * The verdict movement as at most two plain sentences, biggest group first —
 * "74 products went from Fail to Pass." — plus one about products that got worse.
 */
export function movementSentences(movement: VerdictMovement[]): string[] {
  const moved = movement.filter(
    (m) =>
      m.products > 0 &&
      m.preVerdict in VERDICT_RANK &&
      m.postVerdict in VERDICT_RANK &&
      m.preVerdict !== m.postVerdict,
  );
  const better = moved
    .filter((m) => (VERDICT_RANK[m.postVerdict] ?? 0) > (VERDICT_RANK[m.preVerdict] ?? 0))
    .sort((a, b) => b.products - a.products);
  const worse = moved.filter(
    (m) => (VERDICT_RANK[m.postVerdict] ?? 0) < (VERDICT_RANK[m.preVerdict] ?? 0),
  );
  const sentences = better
    .slice(0, 2)
    .map(
      (m) =>
        `${m.products.toLocaleString()} ${m.products === 1 ? 'product' : 'products'} went from ${verdictInfo(m.preVerdict).label} to ${verdictInfo(m.postVerdict).label}.`,
    );
  const worseTotal = worse.reduce((sum, m) => sum + m.products, 0);
  if (worseTotal > 0) {
    sentences.push(
      `${worseTotal.toLocaleString()} ${worseTotal === 1 ? 'product' : 'products'} got a worse verdict than before.`,
    );
  }
  if (sentences.length === 0 && movement.length > 0) {
    sentences.push('No product changed its verdict.');
  }
  return sentences;
}

/** The catalog ("before") text state in words: unreadable, absent from the feed, or the text. */
export function beforeTextNote(before: string | null): string | null {
  if (before === null) return "Couldn't read your catalog for this product.";
  if (before === '') return 'Not in your feed.';
  return null;
}

/** The generated ("after") text state in words, or null when there is text to show. */
export function afterTextNote(after: string | null): string | null {
  if (after === null || after === '') return 'Nothing was written for this field.';
  return null;
}

/**
 * Products still failing a rule in the latest phase (after when it ran, else before), over the
 * products it ran on — the "N of M" the Rules list and "What to fix first" both show.
 */
export function latestFails(
  check: CheckScore,
  showPost: boolean,
): { fails: number; evaluated: number } {
  return showPost
    ? { fails: check.failsPost, evaluated: check.evaluatedPost }
    : { fails: check.failsPre, evaluated: check.evaluatedPre };
}

/** A band verdict for an aggregate score (90+ pass, 70–90 review, below 70 fail); null when unknown. */
export function bandVerdict(score: number | null | undefined): 'PASS' | 'REVIEW' | 'FAIL' | null {
  if (score === null || score === undefined) return null;
  if (score >= PASS_GATE) return 'PASS';
  if (score >= REVIEW_GATE) return 'REVIEW';
  return 'FAIL';
}
